import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useUserContext } from '../../../../../../shared/context/user-context';
import * as Features from '../../../../../../shared/Features';
import FailureSnackbar from '../../../../../../shared/feedback/FailureSnackbar';
import SuccessSnackbar from '../../../../../../shared/feedback/SuccessSnackbar';
import TooltipText from '../../../../../../shared/feedback/TooltipText';
import { useTelemetryContext } from '../../../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../../../telemetry/utils';
import { FilmFile, OrderFilm } from '../../OrderFilmApi';
import SendEmailInput from '../../SendEmailInput';
import { useRemoveFilmFile, useSendFilm, useSetOrderFilms } from '../queries/use-order-films';
import { UploadHandler, UploadHandlerFactory } from '../utils/uploadHelper';
import UploadVideoForm from './UploadVideoForm';
import UploadIndicator from './UploadIndicator';
import SentFilmInformation from './SentFilmInformation';
import VideoFileInformation from './VideoFileInformation';

export type FilmDeliveryLabels = {
    addFileButtonText: string;
    chooseFileButtonText: string;
    uploadedLabel: string;
    downloadButtonTooltip: string;
    removeButtonTooltip: string;
    removeSuccess: string;
    removeFailure: string;
    uploadSuccess: string;
    uploadFailure: string;
    sendFilmInformationLabel: string;
    sendFilmAsEmailButton: string;
    sendFilmAsEmailSuccess: string;
    sendFilmAsEmailFailure: string;
    sendFilmEmailPlaceholder: string;
    deliveredLabel: string;
    notDeliveredLabel: string;
};

type FilmDeliveryProps = {
    orderId: number;
    film: OrderFilm;
    createUploadHandler: UploadHandlerFactory;
    labels: FilmDeliveryLabels;
};

function FilmDelivery({ orderId, film, createUploadHandler, labels }: FilmDeliveryProps) {
    let [uploadHandler, setUploadHandler] = useState<null | UploadHandler>(null);
    let [isUploading, setIsUploading] = useState<boolean>(false);
    let [progress, setProgress] = useState<number>(0);
    let [showUploadSuccessSnackbar, setShowUploadSuccessSnackbar] = useState<boolean>(false);
    let [showUploadFailureSnackbar, setShowUploadFailureSnackbar] = useState<boolean>(false);
    let [showSendEmailSuccessSnackbar, setShowSendEmailSuccessSnackbar] = useState<boolean>(false);
    let [showSendEmailFailureSnackbar, setShowSendEmailFailureSnackbar] = useState<boolean>(false);
    let [showRemoveSuccessSnackbar, setShowRemoveSuccessSnackbar] = useState<boolean>(false);
    let [showRemoveFailureSnackbar, setShowRemoveFailureSnackbar] = useState<boolean>(false);
    let { user } = useUserContext();
    let { logger } = useTelemetryContext();
    let updateFilms = useSetOrderFilms(orderId);

    let canUpload = Features.hasFeature(Features.UploadFilm, user);
    let canSend = Features.hasFeature(Features.SendFilm, user);

    let sendEmailMutation = useSendFilm(orderId, {
        onSuccess: () => setShowSendEmailSuccessSnackbar(true),
        onError: () => setShowSendEmailFailureSnackbar(true)
    });

    let removeFileMutation = useRemoveFilmFile(orderId, {
        onSuccess: () => setShowRemoveSuccessSnackbar(true),
        onError: () => setShowRemoveFailureSnackbar(true)
    });

    function handleChangeChooseFile(file: File) {
        uploadHandler.upload(file);
        setIsUploading(true);
    }

    function handleProgress(value: number) {
        setProgress(value);
    }

    function handleError(error: unknown) {
        let additionalInfo = {
            origin: 'FilmDelivery.handleError on file upload',
            userInfo: getUserInfo(user)
        };
        // @ts-ignore
        logger.logException(error, additionalInfo);

        setIsUploading(false);
        setShowUploadFailureSnackbar(true);
    }

    function handleUploadSuccess(films: OrderFilm[]) {
        updateFilms(films);
        setIsUploading(false);
        setProgress(0);
        setShowUploadSuccessSnackbar(true);
    }

    useEffect(() => {
        if (!isUploading) {
            let handler = createUploadHandler(orderId, film.orderRowId);
            setUploadHandler(handler);

            return () => {
                setUploadHandler(null);
            };
        }
    }, [isUploading]);

    useEffect(() => {
        if (uploadHandler) {
            uploadHandler.onProgress(handleProgress);
            uploadHandler.onError(handleError);
            uploadHandler.onSuccess(handleUploadSuccess);
        }
    }, [uploadHandler]);

    let hasFiles = film.files.length > 0;

    return (
        <Box
            data-testid={`film-delivery-${film.orderRowId}`}
            sx={{
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '4px',
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#f5f5f5',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    borderLeft: '4px solid',
                    borderLeftColor: 'primary.main',
                    padding: '10px 12px'
                }}
            >
                <Typography
                    variant="body1"
                    sx={{
                        flex: '1 auto',
                        fontSize: '14px',
                        fontWeight: 700,
                        lineHeight: '18px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                        wordBreak: 'break-word'
                    }}
                >
                    {film.productName}
                </Typography>
                <Chip
                    size="small"
                    label={film.isDelivered ? labels.deliveredLabel : labels.notDeliveredLabel}
                    color={film.isDelivered ? 'success' : 'default'}
                    variant={film.isDelivered ? 'filled' : 'outlined'}
                    sx={{ marginLeft: '8px', fontSize: '11px' }}
                />
            </Box>

            {film.files.map((file: FilmFile) => {
                let isSent = file.sentTime_Local !== null;

                return (
                    <Box key={file.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: '1 auto', minWidth: 0 }}>
                                <VideoFileInformation
                                    labels={{
                                        downloadButtonTooltip: labels.downloadButtonTooltip,
                                        uploadedLabel: labels.uploadedLabel
                                    }}
                                    filename={file.filename}
                                    uploadedAt={file.uploadedTime_Local}
                                    fileReference={file.reference}
                                />
                            </Box>
                            {canUpload && !isSent ? (
                                <Box sx={{ padding: '0 8px' }}>
                                    <Tooltip
                                        title={
                                            <TooltipText variant="body1">
                                                {labels.removeButtonTooltip}
                                            </TooltipText>
                                        }
                                    >
                                        <IconButton
                                            aria-label={labels.removeButtonTooltip}
                                            onClick={() => removeFileMutation.mutate(file.id)}
                                        >
                                            <DeleteOutlineIcon
                                                sx={{ width: '24px', height: '24px' }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ) : null}
                        </Box>
                        {canSend ? (
                            <SendEmailInput
                                onSend={(email: string) =>
                                    sendEmailMutation.mutate({ orderFilmId: file.id, email })
                                }
                                labels={{
                                    sendButton: labels.sendFilmAsEmailButton,
                                    placeholder: labels.sendFilmEmailPlaceholder
                                }}
                            />
                        ) : null}
                        {canSend && isSent ? (
                            <SentFilmInformation
                                labels={{ sentTo: labels.sendFilmInformationLabel }}
                                sentToEmail={file.sentToEmail}
                                emailSentTime={new Date(file.sentTime_Local)}
                            />
                        ) : null}
                        <Divider />
                    </Box>
                );
            })}

            {isUploading ? <UploadIndicator value={progress} /> : null}

            {canUpload && !isUploading ? (
                <UploadVideoForm
                    onChangeFile={handleChangeChooseFile}
                    inputId={`choose-video-file-${orderId}-${film.orderRowId}`}
                    labels={{
                        buttonLabel: hasFiles
                            ? labels.addFileButtonText
                            : labels.chooseFileButtonText
                    }}
                />
            ) : null}

            <SuccessSnackbar
                open={showUploadSuccessSnackbar}
                text={labels.uploadSuccess}
                onClose={() => setShowUploadSuccessSnackbar(false)}
            />
            <SuccessSnackbar
                open={showSendEmailSuccessSnackbar}
                onClose={() => setShowSendEmailSuccessSnackbar(false)}
                text={labels.sendFilmAsEmailSuccess}
            />
            <SuccessSnackbar
                open={showRemoveSuccessSnackbar}
                onClose={() => setShowRemoveSuccessSnackbar(false)}
                text={labels.removeSuccess}
            />
            <FailureSnackbar
                open={showSendEmailFailureSnackbar}
                onClose={() => setShowSendEmailFailureSnackbar(false)}
                text={labels.sendFilmAsEmailFailure}
            />
            <FailureSnackbar
                open={showUploadFailureSnackbar}
                onClose={() => setShowUploadFailureSnackbar(false)}
                text={labels.uploadFailure}
            />
            <FailureSnackbar
                open={showRemoveFailureSnackbar}
                onClose={() => setShowRemoveFailureSnackbar(false)}
                text={labels.removeFailure}
            />
        </Box>
    );
}

export default FilmDelivery;
